import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { FolderOpen, Trash2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import AudioPlayer from "@/components/AudioPlayer";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyCreations = () => {
  const [subliminals, setSubliminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    fetchSubliminals();
  }, []);

  const fetchSubliminals = async () => {
    try {
      const response = await axios.get(`${API}/subliminals`);
      setSubliminals(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load subliminals");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/subliminals/${id}`);
      setSubliminals(subliminals.filter(s => s.id !== id));
      toast.success("Subliminal deleted");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete subliminal");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-400">
          <Loader2 className="animate-spin" size={24} />
          <span>Loading your creations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3">
            Expand Your Mind.
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            My <span className="text-neon">Creations</span>
          </h1>
          <p className="text-gray-400">
            All your generated subliminals in one place
          </p>
        </motion.div>

        {subliminals.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <FolderOpen size={32} className="text-gray-600" />
            </div>
            <h3 className="text-xl font-medium text-white mb-2">No creations yet</h3>
            <p className="text-gray-400 mb-6">Start creating subliminals to see them here</p>
            <Link to="/create" data-testid="empty-create-btn">
              <NeonButton>Create Your First Subliminal</NeonButton>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            <AnimatePresence mode="popLayout">
              {subliminals.map((sub, index) => (
                <motion.div
                  key={sub.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <GlassCard hover={false} className="relative">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{sub.title}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-500">{formatDate(sub.created_at)}</span>
                          {sub.category && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">
                              {sub.category}
                            </span>
                          )}
                        </div>
                      </div>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            data-testid={`delete-${sub.id}`}
                            className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={18} />
                          </motion.button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#0a0a0f] border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Delete Subliminal?</AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-400">
                              This will permanently delete "{sub.title}". This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(sub.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {/* Affirmations Preview */}
                    <div className="mb-4">
                      <h4 className="text-xs text-gray-500 mb-2">Affirmations</h4>
                      <div className="flex flex-wrap gap-2">
                        {sub.affirmations.slice(0, 3).map((aff, i) => (
                          <span 
                            key={i}
                            className="text-xs px-2 py-1 rounded-lg bg-white/5 text-gray-400 truncate max-w-[200px]"
                          >
                            {aff}
                          </span>
                        ))}
                        {sub.affirmations.length > 3 && (
                          <span className="text-xs px-2 py-1 text-gray-500">
                            +{sub.affirmations.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Settings */}
                    <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
                      <span className="font-mono">{sub.frequency} Hz</span>
                      <span>•</span>
                      <span>Voice: {sub.voice}</span>
                      <span>•</span>
                      <span>Speed: {sub.speed}x</span>
                    </div>

                    {/* Audio Player */}
                    {sub.audio_base64 && (
                      <AudioPlayer
                        audioBase64={sub.audio_base64}
                        title={sub.title}
                        frequency={sub.frequency}
                        loopEnabled={true}
                      />
                    )}
                  </GlassCard>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCreations;
